import { useMemo, useState } from "react";
import {
  MiButton,
  MiList,
  MiMenu,
  MiModalConfirm,
  MiStackedTypography,
} from "@miplatform/components";
import { BUTTON_LABELS, MODAL_TITLES, PAGINATION } from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { getCurrencyString } from "@miplatform/utils";
import { mdiPencilOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { Box, useTheme } from "@mui/material";
import { ConfigPageLayout } from "../../Config/ConfigPageLayout";
import { AddUpdateLaborRate } from "./AddUpdateLaborRate/AddUpdateLaborRate";
import { useManageLaborRatesViewModel } from "./useManageLaborRatesViewModel";

const tabs = [
  {
    label: "All",
    value: "All",
    filter: {},
  },
];

export const ManageLaborRates = () => {
  const [selectedLaborRate, setSelectedLaborRate] = useState(null);
  const [isDeleteModalOpen, handleDeleteModalOpen, handleDeleteModalClose] =
    useBoolean();
  const [
    isAddUpdateModalOpen,
    handleAddUpdateModalOpen,
    handleAddUpdateModalClose,
  ] = useBoolean();

  const {
    laborRates,
    refresh,
    setLaborRates,
    isLoading,
    getLaborRates,
    recentLaborRates,
    setRecentLaborRates,
    getRecentLaborRates,
    deleteLaborRate,
    handleRefresh,
    filter,
    setFilter,
    setSearch,
    debouncedSearch,
    search,
  } = useManageLaborRatesViewModel();
  const theme = useTheme();

  const sections = [
    {
      fieldset: [
        {
          label: "Name",
          field: "name",
        },
        {
          label: "Rate",
          field: "rate",
          renderCell: (row) => (
            <MiStackedTypography
              title="Labor Rate"
              description={getCurrencyString(Math.abs(row.rate))}
            />
          ),
        },
      ],
    },
  ];

  const handleTabChange = (tab) => {
    setFilter(tab?.filter || {});
    setSearch("");
  };

  const handleOpenEditModal = (item) => {
    setSelectedLaborRate(item);
    handleAddUpdateModalOpen();
  };

  const handleCloseConfirmModal = () => {
    setSelectedLaborRate(null);
    handleDeleteModalClose();
  };

  const renderItemActions = (item) => (
    <Box onClick={(e) => e.stopPropagation()}>
      <MiMenu
        testID="ellipsis"
        data={[
          {
            label: BUTTON_LABELS.EDIT,
            icon: mdiPencilOutline,
            action: () => {
              handleOpenEditModal(item);
            },
            color: theme.palette.grey.medium,
          },
          {
            label: BUTTON_LABELS.DELETE,
            icon: mdiTrashCanOutline,
            action: () => {
              setSelectedLaborRate(item);
              handleDeleteModalOpen();
            },
            color: theme.palette.red.main,
          },
        ]}
      />
    </Box>
  );

  const refreshData = useMemo(() => ({ refresh, filter }), [refresh, filter]);

  return (
    <ConfigPageLayout
      title="Labor Rates"
      search={search}
      onHandleSearch={setSearch}
      searchPlaceholder="Search"
      loading={isLoading}
      titleActionComponent={
        <MiButton
          title="Add Labor Rate"
          variant="contained"
          startIcon={{ path: mdiPlus, iconSize: 0.8 }}
          data-testid="add-city-btn"
          onClick={handleAddUpdateModalOpen}
        />
      }
      hideOnBack
    >
      <VerticalTabsLayout
        hideStatusIcons
        tabs={tabs}
        onTabChange={handleTabChange}
        hideNavigationButtons
      >
        <MiList
          viewType="card"
          cardProps={{
            sections,
            renderItemActions,
            onClick: (item) => {
              handleOpenEditModal(item);
            },
          }}
          data={laborRates ?? []}
          loading={isLoading}
          setData={setLaborRates}
          dataProvider={getLaborRates}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          recentData={recentLaborRates}
          setRecentData={setRecentLaborRates}
          recentDataProvider={getRecentLaborRates}
          entityName="Labor Rates"
          refresh={refreshData}
          groupedCriteria={(item) => item.regionCode}
        />
      </VerticalTabsLayout>
      <AddUpdateLaborRate
        selectedLaborRate={selectedLaborRate}
        open={isAddUpdateModalOpen}
        onClose={() => {
          handleAddUpdateModalClose();
          setSelectedLaborRate(null);
        }}
        onRefresh={handleRefresh}
      />
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={isDeleteModalOpen}
        disableClick={isLoading}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.error.main,
        }}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={handleCloseConfirmModal}
        handlePositive={() => {
          deleteLaborRate(selectedLaborRate);
          handleCloseConfirmModal();
        }}
        description={`Are you sure you want to delete labor rate "${selectedLaborRate?.name}"?`}
        buttonPositiveColor="error"
      />
    </ConfigPageLayout>
  );
};
